/**
 * Settings → GitHub Repositories.
 *
 * Persistent, discoverable home for the GitHub App connection state:
 * which installations are linked, which repos they cover (read-only),
 * plus manage-on-GitHub and re-sync actions. Renders nothing when the
 * server has no GitHub App configured (e.g. desktop/local mode).
 */
import { useEffect, useState } from 'react';
import { api } from '../../services/api';
import { _invalidateGithubRepoCache } from './GithubRepoPicker';
import { rememberGithubReturnPath } from './returnPath';

interface Installation {
  installationId: string;
  accountLogin: string;
  accountType: string | null;
  repositorySelection: string | null;
}

interface Repo {
  fullName: string;
  private: boolean;
  htmlUrl: string;
  installationId: string;
}

export function GithubIntegrationSection() {
  const [configured, setConfigured] = useState<boolean | null>(null);
  const [installUrl, setInstallUrl] = useState<string>('');
  const [installations, setInstallations] = useState<Installation[]>([]);
  const [repos, setRepos] = useState<Repo[] | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function load() {
    try {
      const info = await api.getGithubAppInfo();
      setConfigured(Boolean(info.configured));
      if (!info.configured) return;
      setInstallUrl(info.installUrl ?? '');
      const { installations: inst } = await api.listGithubInstallations();
      setInstallations(inst);
      if (inst.length > 0) {
        const { repos: r } = await api.listGithubRepos();
        setRepos(r);
      } else {
        setRepos([]);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load GitHub connection state');
    }
  }

  useEffect(() => { load(); }, []);

  async function onSync() {
    setSyncing(true);
    setError(null);
    try {
      await api.syncGithubInstallations();
      _invalidateGithubRepoCache();
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Sync failed');
    } finally {
      setSyncing(false);
    }
  }

  if (configured === null || configured === false) return null;

  return (
    <section className="border border-border rounded-lg p-6 mt-6">
      <div className="flex items-center justify-between mb-1">
        <h2 className="text-xl font-semibold">GitHub Repositories</h2>
        <div className="flex items-center gap-2">
          <button
            onClick={onSync}
            disabled={syncing}
            className="rounded-md border border-border px-3 py-1.5 text-sm hover:bg-muted transition-colors disabled:opacity-50"
          >
            {syncing ? 'Refreshing…' : 'Refresh'}
          </button>
          <a
            href={installUrl}
            target="_blank"
            rel="noreferrer"
            onClick={() => rememberGithubReturnPath('/settings')}
            className="rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors"
          >
            {installations.length > 0 ? 'Manage on GitHub' : 'Connect repositories'}
          </a>
        </div>
      </div>
      <p className="text-sm text-muted-foreground mb-4">
        Veodiagram reads code through a GitHub App with <span className="font-medium">read-only</span>{' '}
        access to only the repositories you select on GitHub. No write access is ever requested.
      </p>

      {error && (
        <div className="mb-3 rounded-md border border-destructive/20 bg-destructive/10 p-2 text-sm text-destructive">
          {error}
        </div>
      )}

      {installations.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          No repositories connected yet. Click <span className="font-medium">Connect repositories</span> to
          choose which repos veodiagram may read. (Requires signing in with GitHub. After changing the
          selection on GitHub, use Refresh.)
        </p>
      ) : (
        <div className="space-y-3">
          {installations.map((inst) => (
            <div key={inst.installationId} className="rounded-md border border-border/70 p-3">
              <div className="flex items-center gap-2 text-sm">
                <span className="font-medium">{inst.accountLogin}</span>
                <span className="text-muted-foreground">
                  ({inst.accountType ?? 'account'} ·{' '}
                  {inst.repositorySelection === 'all' ? 'all repositories' : 'selected repositories'})
                </span>
              </div>
              <ul className="mt-2 space-y-1">
                {(repos ?? [])
                  .filter((r) => r.installationId === inst.installationId)
                  .map((r) => (
                    <li key={r.fullName} className="flex items-center gap-2 text-sm text-muted-foreground">
                      <span className="inline-block h-1.5 w-1.5 rounded-full bg-emerald-500" aria-hidden />
                      <a href={r.htmlUrl} target="_blank" rel="noreferrer" className="hover:underline">
                        {r.fullName}
                      </a>
                      {r.private && (
                        <span className="rounded border border-border px-1 text-[10px] uppercase tracking-wide">
                          private
                        </span>
                      )}
                    </li>
                  ))}
                {repos === null && <li className="text-sm text-muted-foreground">Loading repositories…</li>}
              </ul>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
